import { TranslateService } from '@ngx-translate/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { EnvironmentInstanceToken } from '../../interfaces/environment.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { AppId } from '../../services/client-manager/client-manager.service';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { ArrayModel } from '../complex/array.model';
import { CompositeModel } from '../complex/composite.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from '../complex/crm-code.model';
import { JsonRadioModel, RadioModel } from '../complex/radio.model';
import { NotificationIdentifierModel } from '../low-level/notification-identifier.model';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModel } from '../primitive/text.model';
import { isDefined } from '../../utilities/is-defined';

export class ConfirmationComplaintsP58Model extends CompositeModel {
    public complaintsQuestion1OptionGroup: RadioModel<TextModel>;
    public complaintsQuestion2OptionGroup: RadioModel<TextModel>;
    public complaintsQuestion3OptionGroup: RadioModel<TextModel>;
    public complaintsQuestion4OptionGroup: RadioModel<TextModel>;
    public complaintsQuestion5OptionGroup: RadioModel<TextModel>;
    public complaintsQuestionnaireLink: string;
    private readonly codeModel: ArrayModel<CrmCodeModel>;
    private readonly hideComplaintsLinkForUserStatusType: boolean;
    public readonly showCyberSecurityComplaintQuestion: boolean;

    /**
     * Prepare a model for the Confirmation Complaints Model.
     *
     * @param notificationId
     * @param userStatus
     * @class
     */
    public constructor(
        public notificationId: NotificationIdentifierModel,
        userStatus: ConfigConstants.USER_STATUS,
    ) {
        super('ConfirmationComplaintsP58Model', {});

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const environment = injector.get(EnvironmentInstanceToken);
        const configService = injector.get(ConfigService);
        const valueHelperService = injector.get(ValueHelperService);
        const questions = valueHelperService.getComplaintQuestions();
        this.showCyberSecurityComplaintQuestion = configService.getShowCyberSecurityQuestion();

        const q1 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion1,
        );
        const q2 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion2,
        );
        const q3 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion3,
        );
        const q4 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion4,
        );
        const q5 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion5,
        );

        const optionGroup: Array<DisplayDecorator<TextModel>> = [];
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_COMPLAINTS_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionYes),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_COMPLAINTS_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionNo),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_COMPLAINTS_OPTION_NONE'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionNA),
            ),
        );

        const complaintsQuestion1 = q1 ? q1.descText : '';
        const complaintsQuestion2 = q2 ? q2.descText : '';
        const complaintsQuestion3 = q3 ? q3.descText : '';
        const complaintsQuestion4 = q4 ? q4.descText : '';

        this.complaintsQuestion1OptionGroup = new RadioModel<TextModel>(
            complaintsQuestion1,
            optionGroup,
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );

        this.complaintsQuestion2OptionGroup = new RadioModel<TextModel>(
            complaintsQuestion2,
            optionGroup,
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );

        this.complaintsQuestion3OptionGroup = new RadioModel<TextModel>(
            complaintsQuestion3,
            optionGroup,
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );

        this.complaintsQuestion4OptionGroup = new RadioModel<TextModel>(
            complaintsQuestion4,
            optionGroup,
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );

        // Create array model for outmodel
        const codes = [
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion1,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion2,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion3,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion4,
                codeGroup: '',
            }),
        ];

        if (this.showCyberSecurityComplaintQuestion && isDefined(q5)) {
            this.complaintsQuestion5OptionGroup = new RadioModel<TextModel>(
                q5.descText,
                optionGroup,
                new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
            );

            codes.push(
                new CrmCodeModel({
                    codeType: 'N',
                    code: ConfigConstants.confirmationComplaintsQuestion5,
                    codeGroup: '',
                }),
            );
        }

        this.codeModel = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet', codes));
        if (environment.appId === AppId.dev58) {
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink(
                ConfigConstants.QCA_LINK_APPS_P58.DEV,
            );
        } else if (environment.appId === AppId.qua58) {
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink(
                ConfigConstants.QCA_LINK_APPS_P58.QUA,
            );
        } else if (environment.appId === AppId.qua58_2) {
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink(
                ConfigConstants.QCA_LINK_APPS_P58.QUA2,
            );
        } else if (environment.appId === AppId.t58) {
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink(
                ConfigConstants.QCA_LINK_APPS_P58.TRAINING,
            );
        } else if (environment.appId === AppId.sandboxp58) {
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink(
                ConfigConstants.QCA_LINK_APPS_P58.SANDBOXE58,
            );
        } else if (environment.appId === AppId.prod58) {
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink();
        } else if (environment.appId === AppId.pwa) {
            // PWA version always runs in P58 context so we can use the same complaints link
            this.complaintsQuestionnaireLink = configService.getComplaintsQuestionnaireLink();
        } else {
            // PACEOnline has no link therefore it must not be set here and its not an error either
            if (environment.appId !== AppId.paceonline58) {
                throw new Error('Complaints link doesnt exists for appID' + environment.appId);
            }
        }
        this.hideComplaintsLinkForUserStatusType = configService.getHideComplaintsForUserStatusType(userStatus);
    }

    public showQuestionnaireLink(): boolean {
        return (
            !this.hideComplaintsLinkForUserStatusType &&
            (this.complaintsQuestion1OptionGroup.selectedEntry.value === ConfigConstants.optionYes ||
                this.complaintsQuestion2OptionGroup.selectedEntry.value === ConfigConstants.optionYes ||
                this.complaintsQuestion3OptionGroup.selectedEntry.value === ConfigConstants.optionYes ||
                this.complaintsQuestion4OptionGroup.selectedEntry.value === ConfigConstants.optionYes ||
                this.complaintsQuestion5OptionGroup?.selectedEntry.value === ConfigConstants.optionYes)
        );
    }

    public getOutModel(): ODataConfirmationComplaintsOutModel {
        // update values in CrmCodeModels
        const q01 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion1,
        );

        const q02 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion2,
        );

        const q03 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion3,
        );

        const q04 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion4,
        );

        const q05 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion5,
        );

        if (!q01 || !q02 || !q03 || !q04 || (this.showCyberSecurityComplaintQuestion && !q05)) {
            throw new Error(`Could not find all required complaint questions!`);
        }

        q01.codeText.value = this.complaintsQuestion1OptionGroup.selectedEntry.value;
        q02.codeText.value = this.complaintsQuestion2OptionGroup.selectedEntry.value;
        q03.codeText.value = this.complaintsQuestion3OptionGroup.selectedEntry.value;
        q04.codeText.value = this.complaintsQuestion4OptionGroup.selectedEntry.value;

        if (this.showCyberSecurityComplaintQuestion && q05) {
            q05.codeText.value = this.complaintsQuestion5OptionGroup.selectedEntry.value;
        }

        return <ODataConfirmationComplaintsOutModel>super.getOutModel();
    }
}

export class ConfirmationComplaintsModelFromJson extends ConfirmationComplaintsP58Model {
    public constructor(
        data: JsonConfirmationComplaintsModel,
        notificationId: NotificationIdentifierModel,
        userStatus: ConfigConstants.USER_STATUS,
    ) {
        super(notificationId, userStatus);
        this.complaintsQuestion1OptionGroup.selectByPredicate(
            (entry) => entry.value === data.complaintsQuestion1OptionGroup.selectedEntry.model.value,
        );
        this.complaintsQuestion2OptionGroup.selectByPredicate(
            (entry) => entry.value === data.complaintsQuestion2OptionGroup.selectedEntry.model.value,
        );
        this.complaintsQuestion3OptionGroup.selectByPredicate(
            (entry) => entry.value === data.complaintsQuestion3OptionGroup.selectedEntry.model.value,
        );
        this.complaintsQuestion4OptionGroup.selectByPredicate(
            (entry) => entry.value === data.complaintsQuestion4OptionGroup.selectedEntry.model.value,
        );
        // Migration logic if notification in progress
        const naOption = 2;
        const selectedEntryValue =
            data.complaintsQuestion5OptionGroup && data.complaintsQuestion5OptionGroup.selectedEntry
                ? data.complaintsQuestion5OptionGroup.selectedEntry.model.value
                : naOption;
        this.complaintsQuestion5OptionGroup?.selectByPredicate((entry) => entry.value === selectedEntryValue);
    }
}

export interface JsonConfirmationComplaintsModel {
    complaintsQuestion1OptionGroup: JsonRadioModel;
    complaintsQuestion2OptionGroup: JsonRadioModel;
    complaintsQuestion3OptionGroup: JsonRadioModel;
    complaintsQuestion4OptionGroup: JsonRadioModel;
    complaintsQuestion5OptionGroup: JsonRadioModel;
}

export interface ODataConfirmationComplaintsOutModel extends ODataObject {
    CrmCodesSet: ODataCrmCodeOutModel[];
}
